import { useMemo } from 'react';
import type { Setup } from '@/types';
import type { UseTuningSession } from '../hooks/useTuningSession';
import { getTuningSpec } from '../data/bow-tuning-specs';
import { BOWS } from '@/data/bows';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { Check, AlertCircle } from 'lucide-react';
import { SafetyNote } from '@/components/SafetyNote';
import { SAFETY_NOTES } from '@/lib/disclaimers';

interface CamTimingData {
  /**
   * Phase A — STATIC CAM LEAN at brace. Both top and bottom cams should point
   * at the nocking point the same way (vertical or matched lean) when checked
   * with a laser or a long arrow shaft laid in the string groove.
   *
   * Per Bow International / Elite Archery / John Dudley (Nock On): "cam lean
   * at brace" (sometimes called pre-lean) is set FIRST, statically, before
   * sync is even tested. Loading shifts from string (at brace) to cables (at
   * full draw), and dynamic lean follows from static lean.
   */
  leanCheckMethod?: 'laser' | 'arrow-shaft' | 'eye';
  /** Top cam lean direction — looking at the back of the bow. */
  topCamLean?: 'aligned' | 'left' | 'right';
  /** Bottom cam lean direction — looking at the back of the bow. */
  bottomCamLean?: 'aligned' | 'left' | 'right';

  /**
   * Phase B — TIMING MARKS at brace. Manufacturer reference dots/holes/lines
   * should line up with the cable per the bow's tune chart. Static check.
   */
  marksAligned?: boolean;

  /**
   * Phase C — CAM SYNC during draw. Both cams hitting their draw stops at the
   * EXACT same moment. Tested on a draw board or with a friend slow-drawing.
   * THIS IS THE DYNAMIC TEST — done AFTER lean + timing are set statically.
   */
  syncTestMethod?: 'draw-board' | 'friend-slow-draw' | 'not-yet';
  /** Did both cams hit their draw stops simultaneously when drawn slowly? */
  simultaneousStop?: boolean;
  /** Which cam hit first if NOT simultaneous? */
  earlyCam?: 'top' | 'bottom' | 'unsure';
}

interface Props {
  tuning: UseTuningSession;
  setup: Setup;
}

/**
 * Step 2 — Cam Timing & Sync
 *
 * Single-cam bows don't need this step (single is its own architecture).
 * Dual-binary cams use a control cable to tie cams together — usually self-syncing.
 * Hybrid cams have a separate timing module — most common cause of "won't paper tune".
 *
 * This step:
 *   1. Looks up the bow's cam architecture from TuningSpec
 *   2. Asks the user to do a slow-draw observation (both cams to stop at same time)
 *   3. Asks about alignment marks at brace (if the bow has them)
 *   4. If off, surfaces the right correction recipe (cable twist or yoke shim) per bow's cableSystem
 */
export function CamTimingStep({ tuning }: Props) {
  const preflight = (tuning.session.stepData['preflight'] as
    | { bowBrand?: string; bowModel?: string; bowYear?: number }
    | undefined) ?? {};
  const data = (tuning.session.stepData['cam_timing'] as CamTimingData | undefined) ?? {};
  const update = (patch: Partial<CamTimingData>) =>
    tuning.updateStepData<CamTimingData>('cam_timing', { ...data, ...patch });

  const bowEntry = useMemo(
    () =>
      preflight.bowBrand && preflight.bowModel && preflight.bowYear
        ? BOWS.find(
            (b) =>
              b.brand === preflight.bowBrand &&
              b.model === preflight.bowModel &&
              b.year === preflight.bowYear,
          )
        : undefined,
    [preflight.bowBrand, preflight.bowModel, preflight.bowYear],
  );
  const tuningSpec = useMemo(
    () =>
      bowEntry ? getTuningSpec(bowEntry.brand, bowEntry.model, bowEntry.year) : null,
    [bowEntry],
  );

  const isSingleCam = tuningSpec?.camArchitecture === 'single';
  const isHybrid = tuningSpec?.camArchitecture === 'hybrid';
  const isYokeTunable = tuningSpec?.cableSystem === 'yoke-tunable';
  const isShimTunable = tuningSpec?.cableSystem === 'shim-tunable';
  const isSnapSpacer = tuningSpec?.cableSystem === 'snap-spacer';
  const isXtsTunable = tuningSpec?.cableSystem === 'xts-tunable';

  // ─────────────────────────────────────────────────────────────────
  // Single-cam bows skip this step entirely
  // ─────────────────────────────────────────────────────────────────
  if (isSingleCam) {
    return (
      <>
        <GoalCard title="No sync needed">
          Your bow uses a single cam + idler wheel. There's no "cam timing" to set
          because there's only one cam to time against. Mark this step complete
          and move on to tiller.
        </GoalCard>
        <InfoCard title="Why this matters on dual-cam bows">
          On dual / hybrid / binary cams, both cams must rotate identically. If one
          stops before the other, the arrow gets uneven force at release — creates
          paper tears that no rest adjustment will fix.
        </InfoCard>
      </>
    );
  }

  // Lean alignment is "good" when both cams are aligned (no lean) OR both lean
  // the same direction (matched lean). Mismatched lean = setup problem.
  const leanMatched =
    data.topCamLean &&
    data.bottomCamLean &&
    data.topCamLean === data.bottomCamLean;

  return (
    <>
      <SafetyNote severity={SAFETY_NOTES.camTiming.severity} title={SAFETY_NOTES.camTiming.title}>
        {SAFETY_NOTES.camTiming.body}
      </SafetyNote>

      <GoalCard title="Three checks in this order — pre-lean, timing, sync">
        <p className="mb-2">
          These three are NOT the same thing — most archers (and a lot of shops)
          conflate them. We do them in order because each builds on the last:
        </p>
        <ol className="list-decimal list-inside space-y-1 ml-1 text-zinc-200">
          <li>
            <strong>Cam lean at brace (pre-lean)</strong> — static check, bow at rest.
            Set with yoke twists.
          </li>
          <li>
            <strong>Timing marks</strong> — static check, bow at rest. Manufacturer
            reference dots/holes line up with the cable.
          </li>
          <li>
            <strong>Cam sync</strong> — dynamic check, on a draw board or slow draw.
            Both cams hit their stops at the EXACT same moment.
          </li>
        </ol>
        <p className="mt-2 text-[12px] text-zinc-400">
          A bow can be perfectly timed at brace yet drift out of sync at full draw if
          cable lengths are off. Sources: Bow International (Grimwood), Elite Archery
          (Sidebottom), Prime/G5 (Cousins), Lancaster Archery Supply.
        </p>
      </GoalCard>

      {bowEntry && tuningSpec && (
        <Card padding="md">
          <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
            Your bow's cam system
          </p>
          <p className="text-sm font-semibold">
            {bowEntry.brand} {bowEntry.model} — {formatArch(tuningSpec.camArchitecture)} cams,{' '}
            {formatCableSys(tuningSpec.cableSystem)}
          </p>
        </Card>
      )}

      {/* ═════════════════════════════════════════════════════════════════
          PHASE A — CAM PRE-LEAN (static, at brace)
          ═════════════════════════════════════════════════════════════════ */}
      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="font-semibold">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ember-500 bg-ember-500/10 border border-ember-500/30 rounded px-1.5 py-0.5 mr-2">
              A
            </span>
            Cam pre-lean (static, at brace)
          </h3>
        </div>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          With the bow at rest, both cams should point at the nocking point the SAME
          WAY — vertical, or matched lean. Use a <strong>Spot Hogg-style laser</strong>{' '}
          OR lay a <strong>long parallel arrow shaft against the cam's string groove</strong>{' '}
          and project the line down the bow. Per Bow International (Liam Grimwood):
          <em className="text-zinc-300">
            {' '}"Holding the arrow against the cam, it should cast a line down the centre
            of the limbs and riser. Do not compare your arrow to the string."
          </em>
        </p>

        <div className="mb-3">
          <label className="text-[11px] text-zinc-500 block mb-1.5">Test method</label>
          <div className="grid grid-cols-3 gap-2">
            <SmallChoice
              label="Laser aligner"
              selected={data.leanCheckMethod === 'laser'}
              onClick={() => update({ leanCheckMethod: 'laser' })}
            />
            <SmallChoice
              label="Parallel arrow shaft"
              selected={data.leanCheckMethod === 'arrow-shaft'}
              onClick={() => update({ leanCheckMethod: 'arrow-shaft' })}
            />
            <SmallChoice
              label="Eyeballed only"
              selected={data.leanCheckMethod === 'eye'}
              onClick={() => update({ leanCheckMethod: 'eye' })}
            />
          </div>
          {data.leanCheckMethod === 'eye' && (
            <p className="text-[10px] text-amber-400/80 mt-1.5">
              Eyeballing only catches gross lean. Use a shaft or laser for any tune work
              that needs to hold past 40 yd.
            </p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <LeanPicker
            label="Top cam lean"
            value={data.topCamLean}
            onChange={(v) => update({ topCamLean: v })}
          />
          <LeanPicker
            label="Bottom cam lean"
            value={data.bottomCamLean}
            onChange={(v) => update({ bottomCamLean: v })}
          />
        </div>

        {/* Lean assessment */}
        {data.topCamLean && data.bottomCamLean && (
          <div className="mt-3 text-[12px] leading-relaxed">
            {leanMatched ? (
              <p className="text-emerald-400">
                <Check size={12} className="inline" /> Cams are matched —
                {data.topCamLean === 'aligned' ? ' both aligned (no static lean).' : ` both lean ${data.topCamLean}, which is fine if intentional (Elite's Sidebottom: "equal static lean at brace as at full draw").`}
              </p>
            ) : (
              <p className="text-rose-400">
                <AlertCircle size={12} className="inline" /> Top cam leans {data.topCamLean}, bottom cam leans {data.bottomCamLean} —
                {' '}mismatched lean. Fix with single-leg yoke twists before testing sync.
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Pre-lean fix tip */}
      {data.topCamLean && data.bottomCamLean && !leanMatched && (
        <TipCard
          title="Fix mismatched cam lean (single-leg yoke twists)"
          defaultOpen
        >
          <p className="mb-2">
            Cam lean is corrected with <strong>single-leg yoke twists</strong> — twisting
            ONE leg of a cable yoke (not both) to pull the cam toward or away from the
            shooter. The method: identify the cam that leans wrong, then twist the
            yoke leg on the OPPOSITE side of the lean direction.
          </p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300 mb-2">
            <li>Press the bow.</li>
            <li>
              For the cam leaning <strong>{data.topCamLean === 'left' || data.bottomCamLean === 'left' ? 'LEFT' : 'RIGHT'}</strong>:
              add 1-2 twists to the <strong>opposite-side</strong> yoke leg (right yoke if
              cam leans left; left yoke if cam leans right — RH shooter convention).
            </li>
            <li>Release press. Re-check with laser/shaft.</li>
            <li>
              Repeat in 1-twist increments. Small changes — yoke twists are sensitive.
            </li>
          </ol>
          <p className="text-[11px] text-zinc-400">
            ⚠ Bow press required. Single-leg twists change cam lean WITHOUT changing total
            cable length — so DL and timing don't shift. Even-twisting both legs would
            shift DL.
          </p>
        </TipCard>
      )}

      {/* ═════════════════════════════════════════════════════════════════
          PHASE B — TIMING MARKS (static, at brace)
          ═════════════════════════════════════════════════════════════════ */}
      <Card padding="lg">
        <h3 className="font-semibold mb-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-ember-500 bg-ember-500/10 border border-ember-500/30 rounded px-1.5 py-0.5 mr-2">
            B
          </span>
          Timing marks at brace (static)
        </h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Most modern cams have alignment marks — dots, holes, or hash marks designed
          to line up with the cable when the bow is at rest. Per Prime Archery (Dave
          Cousins):{' '}
          <em>
            "By just looking at where the cam's timing marks are in relation to the
            limbs you can check the cam timing."
          </em>
        </p>
        <Choice
          label="✓ Marks line up at brace (timing dots concentric with cable)"
          selected={data.marksAligned === true}
          onClick={() => update({ marksAligned: true })}
        />
        <div className="mt-2">
          <Choice
            label="✗ Marks are off — cable doesn't sit centered through the dot/hole"
            selected={data.marksAligned === false}
            onClick={() => update({ marksAligned: false })}
          />
        </div>
      </Card>

      {/* ═════════════════════════════════════════════════════════════════
          PHASE C — CAM SYNC (dynamic, during draw)
          ═════════════════════════════════════════════════════════════════ */}
      <Card padding="lg">
        <h3 className="font-semibold mb-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-ember-500 bg-ember-500/10 border border-ember-500/30 rounded px-1.5 py-0.5 mr-2">
            C
          </span>
          Cam sync — slow-draw observation (dynamic)
        </h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          <strong>This is the dynamic test.</strong> Pre-lean and timing marks were
          static (bow at rest). Sync is checked under load — both cams must hit their
          draw stops at the EXACT same moment as you reach full draw. Best done on a
          <strong> draw board</strong>; second best is slow-drawing with a friend
          watching both stops at eye-level.
        </p>

        <div className="mb-3">
          <label className="text-[11px] text-zinc-500 block mb-1.5">Test method</label>
          <div className="grid grid-cols-3 gap-2">
            <SmallChoice
              label="Draw board"
              selected={data.syncTestMethod === 'draw-board'}
              onClick={() => update({ syncTestMethod: 'draw-board' })}
            />
            <SmallChoice
              label="Friend / slow draw"
              selected={data.syncTestMethod === 'friend-slow-draw'}
              onClick={() => update({ syncTestMethod: 'friend-slow-draw' })}
            />
            <SmallChoice
              label="Haven't tested"
              selected={data.syncTestMethod === 'not-yet'}
              onClick={() => update({ syncTestMethod: 'not-yet' })}
            />
          </div>
        </div>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Sync result</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          With the bow on a draw board, OR drawing slowly with a friend watching,
          observe both cams as you reach full draw. Both should bottom out (hit their
          draw stops) at the same moment.
        </p>

        <div className="space-y-3">
          <Choice
            label="✓ Both cams hit their stops at the same time"
            selected={data.simultaneousStop === true}
            onClick={() => update({ simultaneousStop: true, earlyCam: undefined })}
          />
          <Choice
            label="✗ Top cam hits first"
            selected={data.simultaneousStop === false && data.earlyCam === 'top'}
            onClick={() => update({ simultaneousStop: false, earlyCam: 'top' })}
          />
          <Choice
            label="✗ Bottom cam hits first"
            selected={data.simultaneousStop === false && data.earlyCam === 'bottom'}
            onClick={() => update({ simultaneousStop: false, earlyCam: 'bottom' })}
          />
          <Choice
            label="? Can't tell — need to recheck"
            selected={data.earlyCam === 'unsure'}
            onClick={() => update({ simultaneousStop: false, earlyCam: 'unsure' })}
          />
        </div>
      </Card>

      {/* In-spec celebration */}
      {data.simultaneousStop === true && data.marksAligned === true && (
        <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 flex gap-2.5">
          <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-300">Cams are synced — nice.</p>
            <p className="text-zinc-300 mt-0.5">
              Move to the next step. If a future paper tune shows a horizontal tear that
              won't resolve, the cams have drifted out of sync and you'll be back here.
            </p>
          </div>
        </div>
      )}

      {/* Off-timing fix tip — adapts to the user's cable system */}
      {data.simultaneousStop === false && data.earlyCam && data.earlyCam !== 'unsure' && (
        <TipCard
          title={`Fix: ${data.earlyCam === 'top' ? 'Top' : 'Bottom'} cam is hitting early`}
          defaultOpen
        >
          <p className="mb-3">
            The {data.earlyCam} cam stops rotating before its partner — the cable on the{' '}
            {data.earlyCam === 'top' ? 'top' : 'bottom'} side is too short relative to the
            other side.
          </p>

          {isYokeTunable && (
            <>
              <p className="font-semibold text-zinc-100 mb-1">Yoke twist procedure (yoke-tunable bow):</p>
              <ol className="list-decimal list-inside space-y-1 mb-3 text-zinc-300">
                <li>Press the bow.</li>
                <li>
                  ADD twists to the buss cable on the {data.earlyCam === 'top' ? 'bottom' : 'top'}{' '}
                  yoke leg — this lengthens that side, letting the other cam catch up.
                </li>
                <li>Start with 1-2 twists. Release press. Re-draw and observe.</li>
                <li>Repeat in small steps until both cams stop simultaneously.</li>
              </ol>
              <p className="text-[11px] text-zinc-400">
                ⚠ Bow press required. Even-twisting both yoke legs of the same cable shifts
                draw length — single-leg twists are what change timing without changing DL.
              </p>
            </>
          )}

          {isShimTunable && (
            <>
              <p className="font-semibold text-zinc-100 mb-1">DeadLock shim procedure (Bowtech):</p>
              <ol className="list-decimal list-inside space-y-1 mb-3 text-zinc-300">
                <li>Loosen the cam shim screws on the {data.earlyCam} cam.</li>
                <li>
                  Shift the shim to advance that cam ONE notch in the direction that delays
                  its rotation (cam-specific — consult Bowtech tune chart for direction).
                </li>
                <li>Retighten shim screws. No press needed for this system.</li>
                <li>Re-draw and observe.</li>
              </ol>
            </>
          )}

          {isSnapSpacer && (
            <>
              <p className="font-semibold text-zinc-100 mb-1">PSE EZ.220 Snap Spacer procedure:</p>
              <ol className="list-decimal list-inside space-y-1 mb-3 text-zinc-300">
                <li>
                  Press the bow (most Snap Spacer adjustments still need a press to remove cable load).
                </li>
                <li>
                  Pull and rotate the Snap Spacer on the {data.earlyCam} cam to shift the cable
                  one position toward the timing-delay side.
                </li>
                <li>Re-tension. No tools needed beyond the press.</li>
                <li>Re-draw and observe.</li>
              </ol>
            </>
          )}

          {isXtsTunable && (
            <>
              <p className="font-semibold text-zinc-100 mb-1">Hoyt XTS Tuning System procedure (2026+):</p>
              <ol className="list-decimal list-inside space-y-1 mb-3 text-zinc-300">
                <li>
                  No press needed — XTS adjusts cam timing AND vertical nock travel via
                  on-bow adjusters.
                </li>
                <li>
                  Locate the XTS adjuster on the {data.earlyCam} cam (small Allen-key port
                  on the cam side plate).
                </li>
                <li>
                  Turn the adjuster {data.earlyCam === 'top' ? 'COUNTERCLOCKWISE' : 'CLOCKWISE'} ¼ turn
                  to {data.earlyCam === 'top' ? 'delay the top cam' : 'advance the bottom cam'}.
                </li>
                <li>Re-draw and observe. Repeat in ¼-turn steps.</li>
                <li>Unique to XTS: same procedure also fixes tail-high/low paper tears.</li>
              </ol>
              <p className="text-[11px] text-emerald-400/80">
                ★ Hoyt's XTS is the easiest cam-timing system in archery. Hold the included
                Allen key, no shop visit needed.
              </p>
            </>
          )}

          {!isYokeTunable && !isShimTunable && !isSnapSpacer && !isXtsTunable && (
            <>
              <p className="font-semibold text-zinc-100 mb-1">Generic cable-twist procedure:</p>
              <ol className="list-decimal list-inside space-y-1 mb-3 text-zinc-300">
                <li>Press the bow.</li>
                <li>
                  Remove the buss cable and ADD twists to its{' '}
                  {data.earlyCam === 'top' ? 'bottom' : 'top'} side. Each twist ≈ 1/32" of
                  cable length — small changes have big timing effects.
                </li>
                <li>Reinstall, release press, re-draw, observe.</li>
              </ol>
              <p className="text-[11px] text-zinc-400">
                ⚠ Bow press required. If your bow has no yoke or shim system, this affects
                cam lean too — paper tune after timing-fix.
              </p>
            </>
          )}

          <p className="text-[11px] text-amber-400/80 mt-2">
            After fixing timing, ALL downstream steps need re-verification. Re-check tiller,
            re-shoot paper. We'll flag the steps that need attention in the sidebar.
          </p>
        </TipCard>
      )}

      {data.earlyCam === 'unsure' && (
        <TipCard title="How to tell which cam is early" defaultOpen>
          <p className="mb-2">
            <strong>Best method — draw board:</strong> mount the bow on a draw board, draw
            slowly while watching both cams. Easy to see which hits first.
          </p>
          <p className="mb-2">
            <strong>Friend method:</strong> draw slowly with a friend at eye-level with the
            bow. They watch the cams; you stop drawing the instant they say "stop." Look at
            the gap between each cam's stop and its draw post.
          </p>
          <p>
            <strong>Marker arrow:</strong> some shops use a marked arrow that records where
            it sits in the rest at full draw — comparing top-cam-only vs bottom-cam-only stops.
          </p>
        </TipCard>
      )}

      {data.marksAligned === false && (
        <TipCard title="Marks aren't lined up" defaultOpen>
          <p className="mb-2">
            Timing marks (dots, lines, or slot indicators on the cams) showing off-position
            at brace usually means the same thing as cams hitting at different times. The
            fix is the same — yoke twist or shim adjustment per your cable system.
          </p>
          <p>
            If marks are off but cams hit simultaneously at full draw, the cables may have
            crept (stretched unevenly). Add 1/2 twist evenly to both legs of the cable that
            looks short — gets marks back in spec without shifting timing.
          </p>
        </TipCard>
      )}

      {isHybrid && (
        <InfoCard title="Hybrid cam timing note">
          On hybrid cams (Hoyt HBX, Bear EKO), the top cam is the "control" cam and the
          bottom cam is "driven" by a control cable. Timing adjustments primarily go on
          the control cable, not the buss. Consult your bow's tune chart for the exact
          factory tune mark positions.
        </InfoCard>
      )}

      <WarningCard title="Safety reminder">
        Never press a bow with damaged or worn cables. Inspect strings and cables before
        every press session. If you don't have a bow press, take this step to a pro shop —
        most will time a bow for $20-$40.
      </WarningCard>
    </>
  );
}

// ─── helpers ───

function Choice({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

function formatArch(arch: string): string {
  return ({
    'dual-binary': 'Dual binary',
    'hybrid': 'Hybrid',
    'binary': 'Binary',
    'single': 'Single',
    'crossover': 'Crossover',
  } as Record<string, string>)[arch] ?? arch;
}

function formatCableSys(sys: string): string {
  return ({
    'yoke-tunable': 'yoke-tunable',
    'shim-tunable': 'shim-tunable (DeadLock)',
    'fixed-yoke': 'fixed yoke',
    'snap-spacer': 'snap spacers (PSE EZ.220)',
  } as Record<string, string>)[sys] ?? sys;
}

// Compact 3-up button for test-method picker.
function SmallChoice({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-md border px-2 py-2 text-[11px] text-center transition leading-tight ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

// 3-position lean picker per cam — aligned / left / right.
function LeanPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: 'aligned' | 'left' | 'right' | undefined;
  onChange: (v: 'aligned' | 'left' | 'right') => void;
}) {
  return (
    <div>
      <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>
      <div className="grid grid-cols-3 gap-1.5">
        <LeanBtn label="← LEFT" tone="bad" selected={value === 'left'} onClick={() => onChange('left')} />
        <LeanBtn label="ALIGNED" tone="good" selected={value === 'aligned'} onClick={() => onChange('aligned')} />
        <LeanBtn label="RIGHT →" tone="bad" selected={value === 'right'} onClick={() => onChange('right')} />
      </div>
    </div>
  );
}

function LeanBtn({
  label,
  tone,
  selected,
  onClick,
}: {
  label: string;
  tone: 'good' | 'bad';
  selected: boolean;
  onClick: () => void;
}) {
  const sel = tone === 'good'
    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
    : 'border-rose-500 bg-rose-500/10 text-rose-300';
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-md border px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider transition ' +
        (selected ? sel : 'border-zinc-800 bg-zinc-950 text-zinc-500 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}
